import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';

/**
 * **端末を許可する**（Issue #899。中身は server の synchro_server/device_login.py）。
 *
 * Godot のアプリ（iPhone・iPad・Android・PC）が「Web でログイン」で出したリンク `/device-login?code=ABCD-EFGH` を開く画面。
 * ログインしていなければ AuthGuard がログインの画面へ送り、戻ってくる。「許可する」で本番がその人の新しいトークンを出し、
 * 端末は自分で取りに来る（この画面からは何も渡さない）。コードが無ければ、ほかの端末に出ているコードを打てる。
 */
/** 本番の 400（コードが切れた・使われた・違う）は api.project が「HTTP error! status: 400」にするので、ここで言い直す */
function reason(e) {
  const m = e?.message || String(e);
  return /status: 400/.test(m) ? 'コードが見つかりません（切れたか、もう使われました）。端末でもう一度「Web でログイン」を押してください' : m;
}

export default function DeviceLogin() {
  usePageTitle('端末を許可する');
  const { search } = useLocation();
  const [code, setCode] = useState(() => new URLSearchParams(search).get('code') || '');
  const [typed, setTyped] = useState('');
  const [info, setInfo] = useState(null);
  const [state, setState] = useState(code ? 'loading' : 'input');   // input | loading | ask | sending | done | cancelled | error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!code) return undefined;
    let alive = true;
    setState('loading');
    api.project('GetDeviceLogin', { code }).then((res) => {
      if (!alive) return;
      if (!res?.code) throw new Error(res?.error || 'コードが見つかりません');
      setInfo(res);
      setState(res.approved ? 'done' : 'ask');
    }).catch((e) => { if (alive) { setError(reason(e)); setState('error'); } });
    return () => { alive = false; };
  }, [code]);

  const answer = async (allow) => {
    setState('sending');
    try {
      const res = await api.project('ApproveDeviceLogin', { code, allow });
      if (res?.status !== 'success') throw new Error(res?.error || '許可できませんでした');
      setState(allow ? 'done' : 'cancelled');
    } catch (e) {
      setError(reason(e));
      setState('error');
    }
  };

  return (
    <div className="page" style={{ maxWidth: 560 }}>
      <h2>端末を許可する</h2>
      {state === 'input' && (
        <form onSubmit={(e) => { e.preventDefault(); if (typed.trim()) setCode(typed.trim()); }}>
          <p>端末に出ているコードを入れてください。</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input data-testid="device-code" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="ABCD-EFGH"
              style={{ fontSize: '1.4rem', letterSpacing: 2, width: '12em' }} autoCapitalize="characters" autoComplete="off" />
            <button className="btn btn-primary" type="submit">次へ</button>
          </div>
        </form>
      )}
      {state === 'loading' && <p>確かめています…</p>}
      {(state === 'ask' || state === 'sending') && info && (
        <>
          <p>この端末に、今のログイン（このアカウント）でログインさせますか？</p>
          <p style={{ fontSize: '1.6rem', letterSpacing: 3, margin: '8px 0' }}>{info.code}</p>
          {info.name ? <p>端末: <b>{info.name}</b></p> : null}
          <p style={{ color: '#888', fontSize: '0.85rem' }}>
            自分の端末で「Web でログイン」を押して出たコードのときだけ許可してください。コードは {Math.ceil((info.expires_in || 0) / 60)} 分ほどで切れます。
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={state === 'sending'} onClick={() => answer(true)}>許可する</button>
            <button className="btn btn-secondary" disabled={state === 'sending'} onClick={() => answer(false)}>やめる</button>
          </div>
        </>
      )}
      {state === 'done' && <p>許可しました。端末に戻ってください（数秒でログインします）。このタブは閉じてかまいません。</p>}
      {state === 'cancelled' && <p>やめました。このタブは閉じてかまいません。</p>}
      {state === 'error' && (
        <>
          <p className="error">{error}</p>
          <button className="btn btn-secondary" onClick={() => { setCode(''); setTyped(''); setError(''); setState('input'); }}>コードを入れ直す</button>
        </>
      )}
    </div>
  );
}
